
import 'reflect-metadata';

export const ATTRIBUTE_TYPE_METADATA_KEY = 'attributeType';
export const SERIALIZE_METADATA_KEY = 'attributesToSerialize';
export const ANIMATABLE_METADATA_KEY = 'attributesToAnimate';
export const ALL_METADATA_KEY = 'allAttributes';
export type CommonAttributeSettings = {
     serialize: boolean;
     animatable: boolean;
};
export const DEFAULT_ATTRIBUTE_SETTINGS: CommonAttributeSettings = {
    serialize: true,
    animatable: false,
};
export type CommonMetadataKeys = keyof CommonAttributeSettings | typeof ATTRIBUTE_TYPE_METADATA_KEY;
export type PropDescriptor = PropertyDescriptor & {
    initializer: () => any;
};

export function propagateUpdate(obj: { requiresRedraw?: boolean; requiresUpdate?: boolean; }) {
    if ('requiresRedraw' in obj) {
        obj.requiresRedraw = true;
    }
    if ('requiresUpdate' in obj) {
        obj.requiresUpdate = true;
    }
}

function createGetter(valueStore: WeakMap<object, any>, initialValue: any, isInitialValueOverridden: WeakMap<object, boolean>) {
    return function get(this: any) {
        if (isInitialValueOverridden.get(this)) {
            return valueStore.get(this);
        }
        return initialValue;
    };
}

function createSetter(valueStore: WeakMap<object, any>, initialValue: any, validator: (value: any) => void, isInitialValueOverridden: WeakMap<object, boolean>) {
    return function set(this: any, value: any) {
        validator(value);
        if (!isInitialValueOverridden.get(this) && initialValue === value) {
            return;
        }
        isInitialValueOverridden.set(this, true);
        if (valueStore.get(this) === value) {
            return;
        }
        valueStore.set(this, value);
        propagateUpdate(this);
    };
}

export function defineMetadata(target: object, propertyKey: string, settings: Partial<CommonAttributeSettings>, attributeType: string) {
    (Object.keys(settings) as Array<keyof CommonAttributeSettings>).forEach(key => {
        Reflect.defineMetadata(key, settings[key], target, propertyKey);
    });
    Reflect.defineMetadata(ATTRIBUTE_TYPE_METADATA_KEY, attributeType, target, propertyKey);

    // store some metadata on the class as well about the attribute
    if (settings.serialize) {
        const serializeAttributes = Reflect.getOwnMetadata(SERIALIZE_METADATA_KEY, target) || [];
        serializeAttributes.push(propertyKey);
        Reflect.defineMetadata(SERIALIZE_METADATA_KEY, serializeAttributes, target);
    }
    if (settings.animatable) {
        const animatableAttributes = Reflect.getOwnMetadata(ANIMATABLE_METADATA_KEY, target) || [];
        animatableAttributes.push(propertyKey);
        Reflect.defineMetadata(ANIMATABLE_METADATA_KEY, animatableAttributes, target);
    }
    const allAttributes = Reflect.getOwnMetadata(ALL_METADATA_KEY, target) || [];
    allAttributes.push(propertyKey);
    Reflect.defineMetadata(ALL_METADATA_KEY, allAttributes, target);
}

export function createPropertyDescription(
    target: object,
    propertyKey: string,
    descriptor: PropDescriptor,
    settings: Partial<CommonAttributeSettings>,
    validator: (v: any) => void,
    attributeType: string
): PropertyDescriptor {
    // make sure the initial value of the property also passes validation
    const initialValue = descriptor.initializer();
    try {
        validator(initialValue);
    }
    catch (e) {
        console.error(`Initial value of "${initialValue}" for "${propertyKey}" does not pass the setter validation`);
        throw e;
    }
    // Create the value store for the property. Using a WeakMap will allow the stored value to
    // be garbage collected once the object key is garbage collected. Furthermore, using a
    // WeakMap means the stored value will not prevent the key from getting garbage collected.
    const valueStore = new WeakMap();
    const isInitialValueOverridden = new WeakMap();

    defineMetadata(target, propertyKey, settings, attributeType);

    return {
        configurable: true,
        enumerable: true,
        get: createGetter(valueStore, initialValue, isInitialValueOverridden),
        set: createSetter(valueStore, initialValue, validator, isInitialValueOverridden),
    };
}
